import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Rating,
} from '@mui/material';
import { Close, Person, DirectionsCar, Event, MonetizationOn, Notes } from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2';
import { safeFormatDate } from '@/utils/filterUtils';
import { BookingV2SingleResponse } from '@/types/booking';

interface BookingDetailsModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '80%', md: '700px' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

export const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({ open, onClose, bookingId }) => {
  // Backend V2 single booking endpoint returns nested structure (not flat like list endpoint)
  const [booking, setBooking] = useState<BookingV2SingleResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && bookingId) {
      setIsLoading(true);
      setError(null);
      setBooking(null);

      // Fetch booking by ID using V2 API
      bookingServiceV2.getBookingById(bookingId)
        .then(foundBooking => {
          if (!foundBooking) {
            throw new Error('Booking data is null or undefined');
          }
          setBooking(foundBooking);
        })
        .catch(err => {
          const errorMessage = err.message || 'Failed to fetch booking details. Please try again.';
          console.error('BookingDetailsModal - Error fetching booking:', err);
          setError(errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (open && !bookingId) {
      console.error('❌ BookingDetailsModal opened without bookingId!');
      setError('No booking ID provided');
      setIsLoading(false);
    }
  }, [open, bookingId]);

  const renderContent = () => {
    if (isLoading) {
      return <CircularProgress />;
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!booking) {
      return <Typography>No booking details found.</Typography>;
    }

    return (
      <Grid container spacing={3}>
        {/* Customer Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> Customer
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>{(booking.customer?.name || 'U').charAt(0)}</Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="bold">{booking.customer?.name || 'N/A'}</Typography>
                  <Typography variant="body2" color="text.secondary">{booking.customer?.email || 'N/A'}</Typography>
                  <Typography variant="body2" color="text.secondary">{booking.customer?.phone || 'N/A'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Vehicle Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DirectionsCar /> Vehicle
              </Typography>
              <Typography variant="body1" fontWeight="bold">{booking.vehicle?.name || 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {booking.vehicle?.year || 'N/A'} - {booking.vehicle?.licensePlate || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Booking Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Event /> Booking Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography>{safeFormatDate(booking.booking?.startDate, 'PPpp')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">End Date</Typography>
                  <Typography>{safeFormatDate(booking.booking?.endDate, 'PPpp')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Booking Type</Typography>
                  <Typography>{booking.serviceType || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip label={booking.status || 'pending'} color="primary" size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                  <Typography>{booking.bookingRef || booking.id || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Service Category</Typography>
                  <Typography>{booking.booking?.entityType || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Pricing */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonetizationOn /> Pricing & Payment
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography>Rental Amount:</Typography></Grid>
                <Grid item xs={6}><Typography fontWeight="bold">₦{(booking.payment?.amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                {/* Security Deposit Section - V2 nested structure */}
                {(() => {
                  const deposit = booking.payment?.securityDeposit || 0;
                  const depositStatus = booking.payment?.securityDepositStatus;
                  const heldAt = booking.payment?.securityDepositHeldAt;
                  const releasedAt = booking.payment?.securityDepositReleasedAt;
                  const rentalAmount = booking.payment?.amount || 0;

                  if (!deposit) return null;

                  return (
                    <>
                      <Grid item xs={6}><Typography>Security Deposit:</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold">₦{deposit.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                      <Grid item xs={6}><Typography>Total Paid:</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight="bold" color="primary.main">₦{(rentalAmount + deposit).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                      <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
                        <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                      </Grid>

                      <Grid item xs={6}><Typography fontWeight="600">Deposit Status:</Typography></Grid>
                      <Grid item xs={6}>
                        <Chip
                          label={
                            depositStatus === 'pending' ? '⏳ Pending' :
                            depositStatus === 'held' ? '🟡 Held' :
                            depositStatus === 'released' ? '🟢 Released' :
                            depositStatus === 'refunded' ? '🟢 Refunded' :
                            depositStatus === 'forfeited' ? '🔴 Forfeited' :
                            '⚪ Unknown'
                          }
                          size="small"
                          color={
                            depositStatus === 'released' || depositStatus === 'refunded' ? 'success' :
                            depositStatus === 'held' ? 'warning' :
                            depositStatus === 'pending' ? 'info' :
                            depositStatus === 'forfeited' ? 'error' :
                            'default'
                          }
                        />
                      </Grid>

                      {heldAt && (
                        <>
                          <Grid item xs={6}><Typography variant="body2" color="text.secondary">Held Since:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2">{safeFormatDate(heldAt, 'MMM dd, yyyy HH:mm')}</Typography></Grid>
                        </>
                      )}

                      {releasedAt && (
                        <>
                          <Grid item xs={6}><Typography variant="body2" color="text.secondary">Released On:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2" color="success.main">{safeFormatDate(releasedAt, 'MMM dd, yyyy HH:mm')}</Typography></Grid>
                        </>
                      )}

                      {!releasedAt && depositStatus === 'held' && (
                        <>
                          <Grid item xs={6}><Typography variant="body2" color="text.secondary">Refund Status:</Typography></Grid>
                          <Grid item xs={6}><Typography variant="body2" color="warning.main">Not yet refunded</Typography></Grid>
                        </>
                      )}
                    </>
                  );
                })()}

                <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
                  <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                </Grid>

                <Grid item xs={6}><Typography>Currency:</Typography></Grid>
                <Grid item xs={6}><Typography>NGN</Typography></Grid>
                <Grid item xs={6}><Typography>Payment Method:</Typography></Grid>
                <Grid item xs={6}><Typography>{booking.payment?.method || 'N/A'}</Typography></Grid>
                <Grid item xs={6}><Typography>Payment Status:</Typography></Grid>
                <Grid item xs={6}><Chip label={booking.payment?.status || booking.paymentStatus || 'pending'} size="small" color={(booking.payment?.status || booking.paymentStatus) === 'paid' ? 'success' : 'warning'} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Notes /> Notes
              </Typography>
              <Typography variant="body2">
                {typeof booking.notes === 'object' && booking.notes !== null
                  ? booking.notes.note || 'No additional notes for this booking.'
                  : booking.notes || 'No additional notes for this booking.'}
              </Typography>
              {typeof booking.notes === 'object' && booking.notes !== null && (
                <Box sx={{ mt: 2 }}>
                  {booking.notes.isFirstTimeUser && (
                    <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                      🎉 First-time user discount applied: ₦{booking.notes.firstTimeDiscount?.toLocaleString()}
                    </Typography>
                  )}
                  {booking.notes.totalPaid && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Total paid (including deposit): ₦{booking.notes.totalPaid?.toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            Booking Details
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        {renderContent()}
      </Box>
    </Modal>
  );
};